import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { quotesAPI, usersAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Search, FileText, ChevronRight, GitCompare, X, Trash2, Info, Loader2, AlertTriangle, Clock, GitBranch, FileDown } from 'lucide-react';
import { toast } from 'sonner';
const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const fmt = (n, code) => `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Status definitions (shown as tooltip/legend) ─────────────────────────────
const STATUS_META = {
  DRAFT:    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0',  label: 'DRAFT',    desc: 'Being prepared, not yet shared with the partner.' },
  SENT:     { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd',  label: 'SENT',     desc: 'Shared with the partner for review and approval.' },
  ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0',  label: 'ACCEPTED', desc: 'Partner has accepted - confirmed business.' },
  EXPIRED:  { bg: '#fffbeb', color: '#78350f', border: '#fde68a',  label: 'EXPIRED',  desc: 'Quote has been marked as expired (manually or by date).' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.DRAFT;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap cursor-default"
            style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
            {s.label}
          </span>
        </TooltipTrigger>
        <TooltipContent><p className="text-xs max-w-[180px]">{s.desc}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const PRODUCT_DISPLAY = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro' };
const ProductBadge = ({ code }) => (
  <span className="text-xs font-medium px-2 py-0.5 rounded-full"
    style={code === 'STANDARD'
      ? { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }
      : { background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
    {PRODUCT_DISPLAY[code] || code}
  </span>
);

const CategoryBadge = ({ category }) => {
  if (!category || category === 'PRODUCTION') return null;
  const styles = {
    TEST_SYSTEM: { background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74' },
    BESPOKE:     { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
    MANUAL:      { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
  };
  const labels = { TEST_SYSTEM: 'Test System', BESPOKE: 'Custom', MANUAL: 'Custom' };
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={styles[category] || {}}>
      {labels[category] || category}
    </span>
  );
};

// ── Expiry cell helper ────────────────────────────────────────────────────────
const ManualPricingBadge = () => (
  <span className="text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap"
    style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
    Manual Pricing
  </span>
);

const ExpiryCell = ({ expiryDate, status }) => {
  // Accepted quotes are live contracts — they never expire
  if (!expiryDate || status === 'ACCEPTED') return <span style={{ color: 'hsl(var(--muted-fg))' }}>-</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate); exp.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp - today) / 86400000);
  const fmtd = exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  if (diffDays < 0) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#b91c1c' }}>
        <AlertTriangle size={11} />{fmtd}
      </span>
    );
  }
  if (diffDays <= 7) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#d97706' }}>
        <Clock size={11} />{fmtd}
      </span>
    );
  }
  return <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtd}</span>;
};

// ── Status Legend popover ─────────────────────────────────────────────────────
const StatusLegend = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="flex items-center gap-1 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
          <Info size={13} /> Statuses
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px] p-3">
        <div className="space-y-2">
          {Object.values(STATUS_META).map(s => (
            <div key={s.label} className="flex items-start gap-2">
              <span className="text-xs font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5"
                style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                {s.label}
              </span>
              <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{s.desc}</span>
            </div>
          ))}
          <div className="border-t pt-2 mt-1" style={{ borderColor: 'hsl(var(--border))' }}>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              <span className="font-medium" style={{ color: '#b91c1c' }}>⚠ Red date</span> = past expiry &nbsp;
              <span className="font-medium" style={{ color: '#d97706' }}>⏱ Amber</span> = expiring in \u22647 days
            </p>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// ── Quote Comparison Modal ────────────────────────────────────────────────────
function ComparisonModal({ quoteA, quoteB, onClose }) {
  if (!quoteA || !quoteB) return null;
  const snapA = quoteA.pricing_snapshot || {};
  const snapB = quoteB.pricing_snapshot || {};
  const rows = [
    ['Quote Number', quoteA.quote_number, quoteB.quote_number],
    ['Product', quoteA.product_code, quoteB.product_code],
    ['Users', snapA.num_users || quoteA.num_users, snapB.num_users || quoteB.num_users],
    ['Term (months)', snapA.term_months || quoteA.term_months, snapB.term_months || quoteB.term_months],
    ['Currency', snapA.currency || quoteA.currency, snapB.currency || quoteB.currency],
    ['User Tier', snapA.user_tier_label || 'N/A', snapB.user_tier_label || 'N/A'],
    ['PUPM', snapA.pupm_price != null ? `${CURRENCY_SYMBOLS[snapA.currency] || ''}${Number(snapA.pupm_price).toFixed(2)}` : 'N/A', snapB.pupm_price != null ? `${CURRENCY_SYMBOLS[snapB.currency] || ''}${Number(snapB.pupm_price).toFixed(2)}` : 'N/A'],
    ['Monthly Total', snapA.monthly_total != null ? fmt(snapA.monthly_total, snapA.currency) : 'N/A', snapB.monthly_total != null ? fmt(snapB.monthly_total, snapB.currency) : 'N/A'],
    ['Status', quoteA.status, quoteB.status],
    ['Version', `v${quoteA.version}`, `v${quoteB.version}`],
    ['Expiry', quoteA.expiry_date || 'None', quoteB.expiry_date || 'None'],
  ];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>Quote Comparison</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'hsl(var(--muted))' }}>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Field</th>
                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{quoteA.quote_number}</th>
                <th className="text-left px-3 py-2 text-xs font-semibold" style={{ color: 'hsl(var(--c4p-orange-600))' }}>{quoteB.quote_number}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, valA, valB], i) => {
                const isDiff = String(valA) !== String(valB);
                return (
                  <tr key={label} style={{ background: isDiff ? 'hsl(38 92% 50% / 0.05)' : (i % 2 === 0 ? '#fff' : 'hsl(var(--muted))'), borderBottom: '1px solid hsl(var(--border))' }}>
                    <td className="px-3 py-2 font-medium text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</td>
                    <td className="px-3 py-2 font-semibold">{valA}</td>
                    <td className="px-3 py-2 font-semibold" style={{ color: isDiff ? 'hsl(var(--c4p-orange-600))' : 'inherit' }}>
                      {valB}{isDiff && <span className="ml-1 text-xs" style={{ color: 'hsl(var(--c4p-orange-600))' }}>◄ diff</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main QuotesPage ───────────────────────────────────────────────────────────
export default function QuotesPage() {
  const { isManager, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [psmUsers, setPsmUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [validityFilter, setValidityFilter] = useState('');
  const [psmFilter, setPsmFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showMyQuotes, setShowMyQuotes] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareModal, setCompareModal] = useState(false);
  const [compareQuoteA, setCompareQuoteA] = useState(null);
  const [compareQuoteB, setCompareQuoteB] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, quote_number}
  const [deleting, setDeleting] = useState(false);

  const PAGE_SIZE = 10;

  // Load PSM users for the filter dropdown (managers/admins can see all)
  useEffect(() => {
    if (isManager || isAdmin) {
      usersAPI.list().then(r => {
        const users = Array.isArray(r.data) ? r.data : [];
        setPsmUsers(users.filter(u => u.is_active !== false));
      }).catch(() => {});
    }
  }, [isManager, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search)          params.search        = search;
      if (statusFilter)    params.status        = statusFilter;
      if (productFilter)   params.product_code  = productFilter;
      if (categoryFilter)  params.quote_category = categoryFilter;
      if (validityFilter)  params.validity      = validityFilter;
      if (psmFilter)       params.psm_user_id   = psmFilter;
      if (dateFrom)        params.date_from     = dateFrom;
      if (dateTo)          params.date_to       = dateTo;
      if (showMyQuotes && user?.id) params.owner_user_id = user.id;
      params.sort_by    = sortBy;
      params.sort_order = sortOrder;
      const { data } = await quotesAPI.list(params);
      setQuotes(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, productFilter, categoryFilter, validityFilter, psmFilter, dateFrom, dateTo, showMyQuotes, user?.id, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasFilters = search || statusFilter || productFilter || categoryFilter || validityFilter || psmFilter || dateFrom || dateTo;
  const NAVY = 'hsl(var(--c4p-navy-900))';

  // ── Compare ────────────────────────────────────────────────────────────────
  const toggleCompareSelect = (quoteId) => {
    setSelectedForCompare(prev =>
      prev.includes(quoteId) ? prev.filter(id => id !== quoteId) : prev.length < 2 ? [...prev, quoteId] : prev
    );
  };
  const startComparison = async () => {
    if (selectedForCompare.length !== 2) return toast.error('Select exactly 2 quotes to compare');
    const [a, b] = selectedForCompare;
    try {
      const [rA, rB] = await Promise.all([quotesAPI.get(a), quotesAPI.get(b)]);
      setCompareQuoteA(rA.data); setCompareQuoteB(rB.data); setCompareModal(true);
    } catch { toast.error('Failed to load quotes for comparison'); }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await quotesAPI.delete(deleteTarget.id);
      toast.success(`${deleteTarget.quote_number} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const SortTh = ({ field, children, className = '' }) => {
    const active = sortBy === field;
    return (
      <TableHead className={`cursor-pointer select-none hover:bg-muted/30 ${className}`}
        onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
        <div className="flex items-center gap-1">
          {children}
          {active ? (sortOrder === 'desc' ? <span style={{ fontSize: 10, opacity: 0.7 }}>↓</span> : <span style={{ fontSize: 10, opacity: 0.7 }}>↑</span>) : <span style={{ fontSize: 10, opacity: 0.3 }}>↕</span>}
        </div>
      </TableHead>
    );
  };

  const colSpan = 13 + (compareMode ? 1 : 0);

  // Compute variant labels from the currently-loaded quotes page.
  // Groups quotes by variant_group_id, sorts by created_at, assigns v1/v2...
  // If the anchor quote is on the same page, shows "{anchorNumber} v{n}"; otherwise just "variant".
  const variantLabelMap = useMemo(() => {
    const groups = {};
    for (const q of quotes) {
      if (q.variant_group_id) {
        if (!groups[q.variant_group_id]) groups[q.variant_group_id] = [];
        groups[q.variant_group_id].push(q);
      }
    }
    const map = {};
    for (const [groupId, groupQuotes] of Object.entries(groups)) {
      groupQuotes.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      const anchor = groupQuotes.find(q => q.id === groupId);
      const anchorNumber = anchor?.quote_number;
      groupQuotes.forEach((q, i) => {
        map[q.id] = anchorNumber ? `${anchorNumber} v${i + 1}` : `variant`;
      });
    }
    return map;
  }, [quotes]);

  return (
    <div className="space-y-5" data-testid="quotes-page">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
              {showMyQuotes ? 'My Quotes' : 'All Quotes'}
            </h2>
            {/* My / All toggle - pill segmented control */}
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg" style={{ background: '#e2e8f0' }}>
              <button
                onClick={() => { setShowMyQuotes(true); setPage(1); }}
                className="px-3 py-1 text-xs font-medium rounded-md transition-all outline-none"
                style={{
                  background: showMyQuotes ? 'white' : 'transparent',
                  color: showMyQuotes ? NAVY : '#64748b',
                  boxShadow: showMyQuotes ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
                data-testid="my-quotes-toggle"
              >
                My Quotes
              </button>
              <button
                onClick={() => { setShowMyQuotes(false); setPage(1); }}
                className="px-3 py-1 text-xs font-medium rounded-md transition-all outline-none"
                style={{
                  background: !showMyQuotes ? 'white' : 'transparent',
                  color: !showMyQuotes ? NAVY : '#64748b',
                  boxShadow: !showMyQuotes ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
                data-testid="all-quotes-toggle"
              >
                All Quotes
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{total} {showMyQuotes ? 'of your' : ''} quotes</p>
            <StatusLegend />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export to Excel — Super Admin only */}
          {isAdmin && (
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="export-quotes-xlsx-btn"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('c4p_access_token');
                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/export-xlsx`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error('Export failed');
                  const blob = await res.blob();
                  const url  = URL.createObjectURL(blob);
                  const cd   = res.headers.get('Content-Disposition') || '';
                  const fn   = cd.match(/filename="([^"]+)"/)?.[1] || 'C4P-Quotes-Export.xlsx';
                  const a = document.createElement('a');
                  a.href = url; a.download = fn;
                  document.body.appendChild(a); a.click();
                  document.body.removeChild(a); URL.revokeObjectURL(url);
                } catch { toast.error('Export failed — please try again'); }
              }}>
              <FileDown size={13} /> Export Excel
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { setCompareMode(m => !m); setSelectedForCompare([]); }}
            className="gap-1.5" style={compareMode ? { background: 'hsl(var(--c4p-navy-900))', color: 'white', border: 'none' } : {}}
            data-testid="compare-mode-toggle">
            <GitCompare size={13} /> {compareMode ? 'Exit Compare' : 'Compare'}
          </Button>
          {compareMode && selectedForCompare.length === 2 && (
            <Button size="sm" onClick={startComparison}
              style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }}>
              Compare Selected
            </Button>
          )}
          <Link to="/quotes/new">
            <Button style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-2" data-testid="quotes-new-button">
              <Plus size={15} /> New Quote
            </Button>
          </Link>
        </div>
      </div>

      {compareMode && (
        <div className="rounded-lg p-3 text-sm flex items-center gap-2" style={{ background: 'hsl(var(--accent))', border: '1px solid hsl(var(--c4p-orange-600) / 0.3)' }}>
          <GitCompare size={14} style={{ color: 'hsl(var(--c4p-orange-600))' }} />
          <span>Select 2 quotes to compare. {selectedForCompare.length}/2 selected.</span>
          {selectedForCompare.length > 0 && (
            <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setSelectedForCompare([])}>
              <X size={12} /> Clear
            </Button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex gap-2 flex-wrap items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
            <Input placeholder="Search by number, name, or partner..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-9" data-testid="quotes-search-input" />
          </div>
          {/* Status */}
          <Select value={statusFilter || 'active'} onValueChange={v => { setStatusFilter(v === 'active' ? '' : v); setPage(1); }}>
            <SelectTrigger className="min-w-[170px] w-auto" data-testid="quotes-status-filter"><SelectValue placeholder="Active quotes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active (hide expired)</SelectItem>
              {['DRAFT', 'SENT', 'ACCEPTED', 'EXPIRED'].map(s =>
                <SelectItem key={s} value={s}>{s}</SelectItem>
              )}
            </SelectContent>
          </Select>
          {/* Validity */}
          <Select value={validityFilter || 'all'} onValueChange={v => { setValidityFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="min-w-[170px] w-auto" data-testid="quotes-validity-filter"><SelectValue placeholder="Validity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All validity</SelectItem>
              <SelectItem value="valid">Valid (not expired)</SelectItem>
              <SelectItem value="expiring_soon">Expiring within 7 days</SelectItem>
              <SelectItem value="expired">Past expiry date</SelectItem>
            </SelectContent>
          </Select>
          {/* Product */}
          <Select value={productFilter || 'all'} onValueChange={v => { setProductFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="min-w-[140px] w-auto" data-testid="quotes-product-filter"><SelectValue placeholder="All products" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              <SelectItem value="STANDARD">Standard</SelectItem>
              <SelectItem value="ADVANCED">Advanced</SelectItem>
              <SelectItem value="MTPRO">MTPro</SelectItem>
            </SelectContent>
          </Select>
          {/* Category filter */}
          <Select value={categoryFilter || 'all'} onValueChange={v => { setCategoryFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="min-w-[150px] w-auto" data-testid="quotes-category-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="PRODUCTION">Production</SelectItem>
              <SelectItem value="TEST_SYSTEM">Test System</SelectItem>
              <SelectItem value="BESPOKE">Custom</SelectItem>
              <SelectItem value="MANUAL">Manual Contract</SelectItem>
            </SelectContent>
          </Select>
          {/* PSM filter - managers/admins only */}
          {(isManager || isAdmin) && psmUsers.length > 0 && (
            <Select value={psmFilter || 'all'} onValueChange={v => { setPsmFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="min-w-[140px] w-auto" data-testid="quotes-psm-filter"><SelectValue placeholder="All PSMs" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All PSMs</SelectItem>
                {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>From:</span>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="w-36 h-8 text-sm" data-testid="quotes-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>To:</span>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="w-36 h-8 text-sm" data-testid="quotes-date-to" />
          </div>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={() => {
              setSearch(''); setStatusFilter(''); setProductFilter('');
              setCategoryFilter(''); setValidityFilter(''); setPsmFilter(''); setDateFrom(''); setDateTo(''); setPage(1);
            }} className="gap-1.5 font-medium" data-testid="clear-filters-btn"
              style={{ borderColor: '#ef4444', color: '#ef4444' }}>
              <X size={13} /> Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Mobile card list (< md) */}
      <div className="md:hidden space-y-2">
        {loading ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />) :
         quotes.length === 0 ? (
          <div className="text-center py-12 rounded-xl border bg-white">
            <FileText size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{hasFilters ? 'No quotes match your filters.' : showMyQuotes ? 'You have no quotes yet.' : 'No quotes yet.'}</p>
          </div>
         ) : quotes.map(q => (
          <div key={q.id} onClick={() => navigate(`/quotes/${q.id}`)}
            className="rounded-xl border bg-white p-4 cursor-pointer active:bg-gray-50"
            data-testid={`quote-row-${q.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{q.quote_number}</span>
                  <StatusBadge status={q.status} />
                  {(!q.quote_category || q.quote_category === 'PRODUCTION') && <ProductBadge code={q.product_code} />}
                  <CategoryBadge category={q.quote_category} />
                  {q.quote_category === 'MANUAL' && <ManualPricingBadge />}
                </div>
                {q.quote_name && <p className="text-xs mt-1 truncate font-medium" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{q.quote_name}</p>}
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {q.customer_name || '-'}{q.tenant_name ? ` · ${q.tenant_name}` : ''} · {q.num_users} users
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-sm" style={{ color: 'hsl(var(--c4p-navy-900))' }}>
                  {q.pricing_snapshot ? fmt(q.pricing_snapshot.monthly_total ?? 0, q.currency) + '/mo' : '-'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(q.created_at)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table (≥ md) */}
      <div className="hidden md:block rounded-xl border bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              {compareMode && <TableHead className="w-10" />}
              <SortTh field="quote_number">Quote #</SortTh>
              <TableHead>Billing</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Tenant</TableHead>
              <SortTh field="product_code">Product</SortTh>
              <TableHead>Pricing</TableHead>
              <SortTh field="num_users">Users</SortTh>
              <SortTh field="monthly_total">Monthly</SortTh>
              <SortTh field="status">Status</SortTh>
              <SortTh field="expiry_date">Expiry</SortTh>
              <TableHead>PSM</TableHead>
              <SortTh field="created_at">Created</SortTh>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={colSpan}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
            )) : quotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-12">
                  <FileText size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
                  <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {hasFilters ? 'No quotes match your filters.' : showMyQuotes ? 'You have no quotes yet.' : 'No quotes yet.'}
                  </p>
                  {!hasFilters && showMyQuotes && (
                    <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Switch to <button onClick={() => setShowMyQuotes(false)} className="underline font-medium">All Quotes</button> to see everyone's quotes.
                    </p>
                  )}
                  {!hasFilters && (
                    <Link to="/quotes/new">
                      <Button size="sm" className="mt-3" style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }}>Create Quote</Button>
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ) : quotes.map(q => {
              const isSelected = selectedForCompare.includes(q.id);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const expDate = q.expiry_date ? new Date(q.expiry_date) : null;
              if (expDate) expDate.setHours(0, 0, 0, 0);
              const isPastExpiry = expDate && expDate < today;
              return (
                <TableRow key={q.id}
                  className="data-table-row cursor-pointer"
                  style={isSelected ? { background: 'hsl(var(--accent))' } : isPastExpiry ? { background: '#fef9f0' } : {}}
                  onClick={() => compareMode ? toggleCompareSelect(q.id) : navigate(`/quotes/${q.id}`)}
                  data-testid={`quote-row-${q.id}`}
                >
                  {compareMode && (
                    <TableCell onClick={e => { e.stopPropagation(); toggleCompareSelect(q.id); }}>
                      <div className="w-4 h-4 rounded border-2 flex items-center justify-center"
                        style={{ background: isSelected ? 'hsl(var(--c4p-navy-900))' : 'white', borderColor: isSelected ? 'hsl(var(--c4p-navy-900))' : 'hsl(var(--border))' }}>
                        {isSelected && <span className="text-white text-xs font-bold">{selectedForCompare.indexOf(q.id) + 1}</span>}
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <span className="font-mono text-sm font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{q.quote_number}</span>
                    <span className="text-xs ml-1" style={{ color: 'hsl(var(--muted-fg))' }}>v{q.version}</span>
                    {q.variant_group_id && variantLabelMap[q.id] && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
                          style={{ background: '#dbeafe', color: '#1e40af' }}
                          data-testid={`variant-label-${q.id}`}>
                          <GitBranch size={9} />
                          {variantLabelMap[q.id]}
                        </span>
                        {!q.is_primary_deal_quote && (
                          <span className="text-xs" style={{ color: '#d97706' }}>non-primary</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {q.billing_model === 'TENANT_DIRECT' ? 'Tenant Direct' : q.billing_model === 'DIRECT' ? 'Direct' : 'Partner'}
                  </TableCell>
                  <TableCell className="text-sm max-w-[150px] truncate">{q.customer_name || '\u2014'}</TableCell>
                  <TableCell className="text-sm max-w-[150px] truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{q.tenant_name || '\u2014'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {(!q.quote_category || q.quote_category === 'PRODUCTION') && (
                        <ProductBadge code={q.product_code} />
                      )}
                      <CategoryBadge category={q.quote_category} />
                    </div>
                  </TableCell>
                  <TableCell>
                    {q.quote_category === 'MANUAL' && <ManualPricingBadge />}
                    {q.deal_ref && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                        style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', display: 'inline-block', marginTop: q.quote_category === 'MANUAL' ? '3px' : '0' }}>
                        {q.is_primary_deal_quote === false ? '↳' : '★'} {q.deal_ref.length > 20 ? q.deal_ref.slice(0, 20) + '…' : q.deal_ref}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{q.num_users}</TableCell>
                  <TableCell className="text-sm font-semibold tabular-nums">
                    {q.pricing_snapshot
                      ? fmt(
                          q.billing_model === 'TENANT_DIRECT' && q.pricing_snapshot.tenant_invoice_monthly
                            ? q.pricing_snapshot.tenant_invoice_monthly
                            : q.pricing_snapshot.monthly_total ?? 0,
                          q.currency
                        ) + '/mo'
                      : '\u2014'}
                  </TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell><ExpiryCell expiryDate={q.expiry_date} status={q.status} /></TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{q.psm_name || q.psm_user_name || '-'}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(q.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm"
                        onClick={e => { e.stopPropagation(); setDeleteTarget({ id: q.id, quote_number: q.quote_number }); }}
                        style={{ color: 'hsl(var(--danger))' }}
                        title="Delete quote"
                        data-testid={`delete-quote-${q.id}`}>
                        <Trash2 size={13} />
                      </Button>
                      <ChevronRight size={15} style={{ color: 'hsl(var(--muted-fg))' }} />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: 'hsl(var(--muted-fg))' }}>Page {page} of {totalPages} ({total} quotes)</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} />
              Delete Quote
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{deleteTarget?.quote_number}</strong>?
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-delete-quote">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison modal */}
      {compareModal && compareQuoteA && compareQuoteB && (
        <ComparisonModal quoteA={compareQuoteA} quoteB={compareQuoteB} onClose={() => setCompareModal(false)} />
      )}
    </div>
  );
}
